import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropertyImg1 from '../../Files/Slider1_First_Brick_Images.png';
import PropertyImg2 from '../../Files/Commercial.png';
import PropertyImg3 from '../../Files/Mansion.png';

const images = [PropertyImg1, PropertyImg2, PropertyImg3];

const formatPrice = (value) => {
    const num = Number(value);
    if (isNaN(num) || num <= 0) return 'N/A';

    if (num >= 1e7) {
        return `${(num / 1e7).toFixed(2)} Crore`;
    } else if (num >= 1e5) {
        return `${(num / 1e5).toFixed(2)} Lakh`;
    } else {
        return num.toLocaleString('en-IN'); // fallback for values < 1 Lakh
    }
};

const getPriceAndAreaDetails = (flatTypeArray) => {
    let smallest = null;
    let largest = null;

    flatTypeArray.forEach(ft => {
        if (Array.isArray(ft.builtUpAreas)) {
            ft.builtUpAreas.forEach(area => {
                const price = parseFloat(area.expectedPrice || 0);
                if (!isNaN(price) && price > 0) {
                    if (!smallest || price < parseFloat(smallest.expectedPrice)) smallest = area;
                    if (!largest || price > parseFloat(largest.expectedPrice)) largest = area;
                }
            });
        }
    });

    return { smallest, largest };
};


const HomeProperty = () => {
    const [imageIndexes, setImageIndexes] = useState([]);
    const [data, setData] = useState([]);
    const [showScrollLeft, setShowScrollLeft] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const scrollRef = useRef();
    const navigate = useNavigate();
    const BASE_URL = "https://100real.xyz";

    useEffect(() => {
        setImageIndexes(new Array(data.length).fill(0));
    }, [data]);

    useEffect(() => {
        const interval = setInterval(() => {
            setImageIndexes((prev) =>
                prev.map((index, idx) => {
                    const imagesLen = data[idx]?.formImages?.length || 1;
                    return imagesLen > 1 ? (index + 1) % imagesLen : 0;
                })
            );
        }, 3000);
        return () => clearInterval(interval);
    }, [data]);

    const fetchAll = async () => {
        try {
            const [res1, res2, res3] = await Promise.all([
                fetch(`${BASE_URL}/resident/getproperty/HBTR0007`),
                fetch(`${BASE_URL}/commercial/getproperty/HBTR0007`),
                fetch(`${BASE_URL}/rentresident/getproperty/HBTR0007`)
            ]);

            const [json1, json2, json3] = await Promise.all([
                res1.json(),
                res2.json(),
                res3.json()
            ]);

            const parseProps = (item) => {
                const parseJSON = (val) => {
                    try {
                        return typeof val === 'string' ? JSON.parse(val) : val;
                    } catch {
                        return val;
                    }
                };
                return {
                    amenities: parseJSON(item.amenities || "[]"),
                    carpetArea: parseJSON(item.carpetArea || "[]"),
                    flatType: parseJSON(item.flatType || "[]"),
                    expectedPrice: parseJSON(item.expectedPrice || "[]"),
                    formImages: parseJSON(item.formImages || "[]"),
                    locationAdvantage: parseJSON(item.locationAdvantage || item.locationAdvantages || "[]"),
                };
            };

            const combined = [
                ...json1.map(item => ({ ...item, type: "resident", ...parseProps(item) })),
                ...json2.map(item => ({ ...item, type: "commercial", ...parseProps(item) })),
                ...json3.map(item => ({ ...item, type: "rent", ...parseProps(item) }))
            ];

            const uniqueData = combined.filter(
                (value, index, self) =>
                    index === self.findIndex((t) => t.property_id === value.property_id)
            );

            const sortedData = uniqueData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setData(sortedData);
        } catch (error) {
            console.log("Error loading data:", error);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handlePageChange = (propertyId, propertyType) => {
        if (propertyType === "Residential") {
            navigate(`/residential-property/${propertyId}`);
        } else if (propertyType === "Commercial") {
            navigate(`/commercial-property/${propertyId}`);
        } else if (propertyType === "Rent Residential") {
            navigate(`/rent-property/${propertyId}`);
        }
    };

    const formatCarpetAreas = (flatTypes = []) => {
        const areaSet = new Set();

        flatTypes.forEach(ft => {
            if (Array.isArray(ft.builtUpAreas)) {
                ft.builtUpAreas.forEach(areaObj => {
                    const area = parseFloat(areaObj?.value);
                    if (!isNaN(area)) {
                        areaSet.add(area);
                    }
                });
            }
        });

        const sortedAreas = [...areaSet].sort((a, b) => a - b);
        const areaString = sortedAreas.length > 0 ? `${sortedAreas.join(', ')} sqft` : '';

        // ✅ Trim to max 20 characters (add ellipsis if needed)
        return areaString.length > 20 ? areaString.slice(0, 17) + '...' : areaString;
    };


    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const updateScrollVisibility = () => {
        const el = scrollRef.current;
        if (el) {
            setShowScrollLeft(el.scrollLeft > 0);
            setShowScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
        }
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateScrollVisibility();
        el.addEventListener('scroll', updateScrollVisibility);
        window.addEventListener('resize', updateScrollVisibility);

        return () => {
            el.removeEventListener('scroll', updateScrollVisibility);
            window.removeEventListener('resize', updateScrollVisibility);
        };
    }, [data]);

    return (
        <div className="properties-main-first-brick">
            <div className="property-section-heading-first-brick">
                <h1>Find Your Dream Home</h1>
                <p>Browse premium listings across top cities in India.</p>
            </div>

            {showScrollLeft && (
                <button
                    className="property-card-scroll-btn-first-brick property-card-left-first-brick"
                    onClick={() => scroll('left')}
                >
                    <i className="fa-solid fa-chevron-left"></i>
                </button>
            )}

            <div className="property-cards-first-brick" ref={scrollRef}>
                {data.length === 0 ? (
                    <div className="no-properties-message">No properties available.</div>
                ) : (
                    data.map((property, idx) => {
                        const { smallest, largest } = getPriceAndAreaDetails(property.flatType);
                        const images = property.formImages.slice(0, 3);
                        const isSlider = images.length > 1;

                        return (
                            <div className="property-card-first-brick" key={property.property_id}
                                onClick={() => handlePageChange(property.property_id, property.typeofproperty)}
                                style={{ cursor: "pointer" }}>
                                <div className="ribbon">
                                    {property.typeofproperty?.charAt(0).toUpperCase() +
                                        property.typeofproperty?.slice(1).toLowerCase() || 'Property'}
                                </div>

                                <div className="property-card-images-first-brick">
                                    {isSlider ? (
                                        <>
                                            {images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={`https://100real.xyz/Images/Store/${img}`}
                                                    alt={`${property.building_name} view ${i + 1}`}
                                                    style={{
                                                        opacity: imageIndexes[idx] === i ? 1 : 0,
                                                        transition: 'opacity 0.6s ease-in-out',
                                                        zIndex: imageIndexes[idx] === i ? 2 : 1,
                                                        position: 'absolute',
                                                    }}
                                                />
                                            ))}
                                            <div className="property-card-images-pagination-first-brick">
                                                {images.map((_, i) => (
                                                    <span
                                                        key={i}
                                                        className={`dash ${imageIndexes[idx] === i ? 'active' : ''}`}
                                                        onClick={() => {
                                                            const newIndexes = [...imageIndexes];
                                                            newIndexes[idx] = i;
                                                            setImageIndexes(newIndexes);
                                                            handlePageChange(property.property_id, property.typeofproperty)
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                    ></span>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={`https://100real.xyz/Images/Store/${images[0]}`}
                                            alt={`${property.building_name}`}
                                            style={{
                                                opacity: 1,
                                                zIndex: 2,
                                                position: 'absolute',
                                                cursor: "pointer"
                                            }}
                                            onClick={() => handlePageChange(property.property_id, property.typeofproperty)}
                                        />
                                    )}
                                </div>

                                <div className="property-card-content-first-brick">
                                    <div
                                        className="property-card-building-name-first-brick"
                                        onClick={() => handlePageChange(property.property_id, property.typeofproperty)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <h1>{property.building_name}</h1>
                                    </div>

                                    {(smallest?.expectedPrice > 0 || largest?.expectedPrice > 0) && (
                                        <div className="property-card-pricing-first-brick">
                                            <h3>
                                                {smallest?.expectedPrice > 0 && largest?.expectedPrice > 0 ? (
                                                    smallest.expectedPrice === largest.expectedPrice ? (
                                                        `₹ ${formatPrice(smallest.expectedPrice)}`
                                                    ) : (
                                                        `₹ ${formatPrice(smallest.expectedPrice)} - ₹ ${formatPrice(largest.expectedPrice)}`
                                                    )
                                                ) : smallest?.expectedPrice > 0 ? (
                                                    `₹ ${formatPrice(smallest.expectedPrice)}`
                                                ) : (
                                                    `₹ ${formatPrice(largest.expectedPrice)}`
                                                )}
                                            </h3>
                                        </div>
                                    )}

                                    <div className="property-card-carpet-address-first-brick">
                                        <p>{formatCarpetAreas(property.flatType)} at {property.city}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {showScrollRight && (
                <button
                    className="property-card-scroll-btn-first-brick property-card-right-first-brick"
                    onClick={() => scroll('right')}
                >
                    <i className="fa-solid fa-chevron-right"></i>
                </button>
            )}
        </div>
    );
};

export default HomeProperty;
